import { z } from "zod";
import type { ContentPack } from "@stormglass/game-core";

const id = z
  .string()
  .min(1)
  .regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/);
const positive = z.number().positive();
const nonnegative = z.number().nonnegative();
const positiveInteger = z.number().int().positive();

const contentPackSchema = z.object({
  schemaVersion: positiveInteger,
  contentVersion: z.string().min(1),
  simulationVersion: positiveInteger,
  enemies: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      baseHealth: positive,
      speedPerSecond: positive,
      impactDamage: positiveInteger,
      chargeReward: nonnegative,
      affinityId: id,
      radius: positive,
      behavior: z.enum(["radial", "spiral", "split-on-death"]),
      splitInto: z.object({ enemyId: id, count: positiveInteger }).optional(),
      displacementResistance: z.number().min(0).max(1),
      earliestRound: positiveInteger,
      spawnWeight: positive,
    }),
  ),
  eliteModifiers: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      earliestRound: positiveInteger,
      weight: positive,
      healthMultiplier: positive,
      speedMultiplier: positive,
      mitigation: z.number().min(0).max(0.95),
      regenerationPerSecond: nonnegative,
      displacementResistance: z.number().min(0).max(1),
      affinityOverride: id.optional(),
    }),
  ),
  weapons: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      kind: z.enum(["lightning", "rain", "wind", "hail"]),
      behavior: z.enum([
        "pulse-single-target",
        "cluster-zone",
        "gust-area",
        "heavy-splash",
      ]),
      targetingProfile: id,
      damageType: z.enum([
        "lightning",
        "rain",
        "wind",
        "hail",
        "reaction",
        "impact",
      ]),
      baseStats: z.object({
        damage: nonnegative,
        intervalTicks: positiveInteger,
        criticalChance: z.number().min(0).max(1),
        criticalMultiplier: positive,
        radius: nonnegative,
        durationTicks: z.number().int().nonnegative(),
        statusId: id.optional(),
        statusStacks: z.number().int().nonnegative(),
        displacement: nonnegative,
      }),
    }),
  ),
  upgrades: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      category: z.enum(["basic", "major", "boss", "permanent"]),
      weaponKind: z.enum(["lightning", "rain", "wind", "hail"]).optional(),
      rarity: z.enum(["common", "rare", "epic", "legendary"]),
      prerequisites: z.array(
        z.object({
          kind: z.enum([
            "upgrade",
            "weapon-owned",
            "permanent-owned",
            "round-at-least",
          ]),
          id: z.string().optional(),
          value: z.number().optional(),
        }),
      ),
      exclusions: z.array(
        z.object({
          kind: z.enum([
            "upgrade",
            "weapon-owned",
            "permanent-owned",
            "round-at-least",
          ]),
          id: z.string().optional(),
          value: z.number().optional(),
        }),
      ),
      maxLevel: positiveInteger,
      baseCost: nonnegative,
      costGrowth: positive,
      effects: z.array(
        z.object({
          stat: z.string().min(1),
          operation: z.enum(["add", "multiply", "unlock"]),
          value: z.union([z.number(), z.string().min(1)]),
        }),
      ),
      offerWeight: nonnegative,
      complexity: positiveInteger,
      guaranteedOfferRound: positiveInteger.optional(),
    }),
  ),
  statuses: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      stackPolicy: z.enum(["add", "replace", "max"]),
      maxStacks: positiveInteger,
      refreshPolicy: z.enum(["refresh", "extend", "none"]),
      durationTicks: positiveInteger,
      bossEffectiveness: z.number().min(0).max(1),
      movementMultiplierPerStack: z.number().min(0).max(1),
      interactionTags: z.array(z.string()),
    }),
  ),
  affinities: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      incomingDamageMultipliers: z.record(z.string(), positive),
      statusModifiers: z.record(z.string(), nonnegative),
    }),
  ),
  reactions: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      incomingDamageType: z.enum([
        "lightning",
        "rain",
        "wind",
        "hail",
        "reaction",
        "impact",
      ]),
      requiredStatusId: id.optional(),
      requiredStatusStacks: positiveInteger.optional(),
      requiredWeaponKinds: z
        .array(z.enum(["lightning", "rain", "wind", "hail"]))
        .min(2),
      consumption: z.enum(["none", "one-stack", "all-stacks"]),
      bonusDamageMultiplier: nonnegative,
      splashRadius: nonnegative,
      displacement: nonnegative,
      applyStatusId: id.optional(),
      applyStatusStacks: positiveInteger.optional(),
      internalCooldownTicks: z.number().int().nonnegative(),
      requiresCoupler: z.boolean(),
      zoneBehavior: z.literal("move-rain").optional(),
    }),
  ),
  spawnPatterns: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      behavior: z.enum([
        "scattered",
        "clustered",
        "ring",
        "alternating",
        "spiral",
        "portal",
        "mixed",
      ]),
      earliestRound: positiveInteger,
    }),
  ),
  bosses: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      movementProfile: z.literal("timed-direct"),
      durationTicks: positiveInteger,
      baseHealth: positive,
      healthScalePerTier: positive,
      impactDamage: positiveInteger,
      affinityId: id,
      rewardCharge: nonnegative,
      rewardCondensate: nonnegative,
      abilities: z.array(
        z.object({
          kind: z.enum(["summon", "mitigation", "weapon-slow"]),
          triggerRemainingTicks: z.number().int().nonnegative(),
          parameters: z.record(z.string(), z.unknown()),
        }),
      ),
    }),
  ),
  rounds: z
    .array(
      z.object({
        round: positiveInteger,
        quota: positiveInteger,
        spawnIntervalTicks: positiveInteger,
        spawnPatternId: id,
        enemyWeights: z.record(id, positive),
        bossId: id.optional(),
      }),
    )
    .min(1),
  defenses: z.record(
    id,
    z.object({
      id,
      schemaVersion: positiveInteger,
      kind: z.enum(["shield", "integrity", "repulsion"]),
      cost: nonnegative,
      unlockRound: positiveInteger,
      parameters: z.record(z.string(), z.number()),
    }),
  ),
  economy: z.object({
    passiveChargePerSecond: nonnegative,
    firstWeatherCost: nonnegative,
    condensatePerRound: nonnegative,
    condensatePerBoss: nonnegative,
    firstClearBonus: z.record(z.string(), nonnegative),
  }),
});

export interface ContentValidationIssue {
  readonly path: string;
  readonly message: string;
}

function collectReferenceIssues(
  content: ContentPack,
): ContentValidationIssue[] {
  const issues: ContentValidationIssue[] = [];
  const issue = (path: string, message: string): void => {
    issues.push({ path, message });
  };
  const assertKeysMatchIds = (
    record: Readonly<Record<string, { readonly id: string }>>,
    path: string,
  ): void => {
    for (const [key, value] of Object.entries(record))
      if (key !== value.id)
        issue(
          `${path}.${key}.id`,
          `Record key must match definition id ${value.id}.`,
        );
  };
  assertKeysMatchIds(content.enemies, "enemies");
  assertKeysMatchIds(content.eliteModifiers, "eliteModifiers");
  assertKeysMatchIds(content.weapons, "weapons");
  assertKeysMatchIds(content.upgrades, "upgrades");
  assertKeysMatchIds(content.statuses, "statuses");
  assertKeysMatchIds(content.affinities, "affinities");
  assertKeysMatchIds(content.reactions, "reactions");
  assertKeysMatchIds(content.spawnPatterns, "spawnPatterns");
  assertKeysMatchIds(content.bosses, "bosses");
  assertKeysMatchIds(content.defenses, "defenses");
  for (const enemy of Object.values(content.enemies)) {
    if (content.affinities[enemy.affinityId] === undefined)
      issue(`enemies.${enemy.id}.affinityId`, "Unknown affinity.");
    if (
      enemy.splitInto !== undefined &&
      content.enemies[enemy.splitInto.enemyId] === undefined
    )
      issue(`enemies.${enemy.id}.splitInto`, "Unknown split enemy.");
  }
  for (const modifier of Object.values(content.eliteModifiers))
    if (
      modifier.affinityOverride !== undefined &&
      content.affinities[modifier.affinityOverride] === undefined
    )
      issue(
        `eliteModifiers.${modifier.id}.affinityOverride`,
        "Unknown affinity.",
      );
  for (const weapon of Object.values(content.weapons))
    if (
      weapon.baseStats.statusId !== undefined &&
      content.statuses[weapon.baseStats.statusId] === undefined
    )
      issue(`weapons.${weapon.id}.baseStats.statusId`, "Unknown status.");
  for (const upgrade of Object.values(content.upgrades))
    for (const requirement of [
      ...upgrade.prerequisites,
      ...upgrade.exclusions,
    ]) {
      if (
        requirement.kind === "upgrade" &&
        content.upgrades[requirement.id ?? ""] === undefined
      )
        issue(
          `upgrades.${upgrade.id}.requirements`,
          `Unknown upgrade ${requirement.id ?? ""}.`,
        );
    }
  for (const reaction of Object.values(content.reactions)) {
    if (
      reaction.requiredStatusId !== undefined &&
      content.statuses[reaction.requiredStatusId] === undefined
    )
      issue(`reactions.${reaction.id}.requiredStatusId`, "Unknown status.");
    if (
      reaction.applyStatusId !== undefined &&
      content.statuses[reaction.applyStatusId] === undefined
    )
      issue(`reactions.${reaction.id}.applyStatusId`, "Unknown status.");
  }
  const seenRounds = new Set<number>();
  for (const round of content.rounds) {
    if (seenRounds.has(round.round))
      issue(`rounds.${String(round.round)}`, "Duplicate round definition.");
    seenRounds.add(round.round);
    if (content.spawnPatterns[round.spawnPatternId] === undefined)
      issue(
        `rounds.${String(round.round)}.spawnPatternId`,
        "Unknown spawn pattern.",
      );
    if (
      round.bossId !== undefined &&
      content.bosses[round.bossId] === undefined
    )
      issue(`rounds.${String(round.round)}.bossId`, "Unknown boss.");
    for (const enemyId of Object.keys(round.enemyWeights))
      if (content.enemies[enemyId] === undefined)
        issue(
          `rounds.${String(round.round)}.enemyWeights.${enemyId}`,
          "Unknown enemy.",
        );
  }
  for (const boss of Object.values(content.bosses))
    if (content.affinities[boss.affinityId] === undefined)
      issue(`bosses.${boss.id}.affinityId`, "Unknown affinity.");
  return issues;
}

export function validateContentPack(input: unknown): ContentPack {
  const parsed = contentPackSchema.parse(input) as ContentPack;
  const issues = collectReferenceIssues(parsed);
  if (issues.length > 0)
    throw new Error(
      `Invalid content references:\n${issues.map((entry) => `${entry.path}: ${entry.message}`).join("\n")}`,
    );
  return parsed;
}

export function inspectContentPack(
  input: unknown,
): readonly ContentValidationIssue[] {
  const result = contentPackSchema.safeParse(input);
  if (!result.success)
    return result.error.issues.map((entry) => ({
      path: entry.path.join("."),
      message: entry.message,
    }));
  return collectReferenceIssues(result.data as ContentPack);
}
